<template>
  <div class="add-group">
    <div>
      <div class="add-group-header">
        <el-radio-group v-model="dayType" @change="getGroups">
          <el-radio label="odd">{{ $t("Admin.odd-days") }}</el-radio>
          <el-radio label="even">{{ $t("Admin.even-days") }}</el-radio>
          <el-radio label="other">{{ $t("Admin.other") }}</el-radio>
        </el-radio-group>
        <button v-if="props.showFullscreen" @click="toggleFullscreen">
          <Icon icon="gridicons:fullscreen" width="24" height="24" />
        </button>
      </div>
      <div
        class="slots"
        v-loading="loading"
        ref="slotsRef"
        tabindex="0"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseLeave"
        @keydown="onKeyDown"
        @mouseenter="focusSlots"
        :class="{
          '!h-[70vh]': isAdd,
          'is-dragging': isDragging,
        }"
        :style="
          !isAdd
            ? { height: 'calc(100vh - ' + props.heightOffset + 'px)' }
            : null
        "
      >
        <button
          @click="toggleFullscreen"
          v-if="isFullscreen"
          class="absolute top-1 right-1 z-20"
        >
          <Icon icon="gridicons:fullscreen" width="24" height="24" />
        </button>
        <table class="slots-table">
          <thead>
            <tr>
              <th>{{ $t("Admin.slots") }}</th>
              <th v-for="slot in slots" :key="slot">
                {{ slot.slice(0, 5) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="room in rooms" :key="room">
              <td class="room-name">{{ room }}</td>
              <template v-for="slotKey in slots" :key="slotKey">
                <td
                  v-if="
                    schedule[room] &&
                    schedule[room][slotKey] &&
                    schedule[room][slotKey] !== 'covered'
                  "
                  :colspan="
                    Array.isArray(schedule[room][slotKey])
                      ? schedule[room][slotKey][0].colspan
                      : schedule[room][slotKey].colspan
                  "
                  class="group-cell"
                >
                  <!-- Single group -->
                  <ScheduleGroup
                    v-if="!Array.isArray(schedule[room][slotKey])"
                    :group="schedule[room][slotKey]"
                    :is-selected="
                      selectedGroup &&
                      selectedGroup === schedule[room][slotKey].id
                    "
                    @group-selected="handleGroupSelected"
                    :hasRadio="isAdd"
                  />
                  <!-- Multiple groups -->
                  <div v-else class="multiple-groups">
                    <ScheduleGroup
                      v-for="group in schedule[room][slotKey]"
                      :key="group.id"
                      :group="group"
                      :is-selected="selectedGroup && selectedGroup === group.id"
                      @group-selected="handleGroupSelected"
                      :hasRadio="isAdd"
                      class="multiple-group-item"
                    />
                  </div>
                </td>
                <td
                  v-else-if="schedule[room] && schedule[room][slotKey] === null"
                  class="empty-cell"
                >
                  <!-- Empty cell -->
                </td>
                <!-- Cells that are 'covered' by a colspan are not rendered -->
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <button
      v-if="isAdd"
      class="mb-2 next-step-btn"
      type="submit"
      :disabled="loading"
      v-loading="loading"
      @click="addLeadToGroup"
    >
      Guruhga qo'shish
    </button>
    <div class="flex justify-center">
      <div class="mt-4 alert-container" v-if="props.hasAlert">
        <p>
          <span class="text-red-500">*</span>Guruhga qo'shilgan o'quvchiga
          "sinov darsi" statusi beriladi va birinchi darsga qorzdorlik
          yozilmaydi
        </p>
      </div>
    </div>
  </div>
</template>
<script setup>
/* global defineProps, defineEmits */
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";
import api from "@/apiConfig/api";
import { DateTime } from "luxon";
import ScheduleGroup from "./ScheduleGroup.vue";
import { Icon } from "@iconify/vue";
import apiV2 from "@/apiConfig/api-v2";
import { ElMessage } from "element-plus";
import { decryptText } from "@/utils/cryptFunc";
import { getAdminInfo } from "@/modules/Admin/pages/AdminMain/api/index";

const { t } = useI18n();

const role = ref(decryptText(localStorage.getItem("role")));

// Get branch_id with fallback to localStorage and /me API for kurator
const getBranchId = async () => {
  let branch_id = store.state.branches.active_branch;

  // If store doesn't have branch_id, get it from localStorage and update store
  if (!branch_id) {
    const branchIdFromStorage = localStorage.getItem("branch_id");
    if (branchIdFromStorage) {
      branch_id = +branchIdFromStorage;
      // Update store with branch_id from localStorage
      store.commit("branches/setActiveBranchId", branch_id);
    } else if (role.value === "kurator") {
      // If kurator and no branch_id, get it from /me API
      try {
        const res = await getAdminInfo();
        const branches = res.data?.branches || [];
        if (branches.length > 0) {
          branch_id = branches[0]?.id;
          // Update store and localStorage with branch_id
          store.commit("branches/setActiveBranchId", branch_id);
          localStorage.setItem("branch_id", branch_id);
        }
      } catch (err) {
        console.error("Failed to get admin info:", err);
      }
    }
  }

  return branch_id;
};

const branchId = computed(() => store.state.branches.active_branch);
const props = defineProps({
  hasAlert: {
    type: Boolean,
    default: false,
  },
  leadInfo: {
    type: Object,
    default: null,
  },
  isAdd: {
    type: Boolean,
    default: false,
  },
  showFullscreen: {
    type: Boolean,
    default: true,
  },
  heightOffset: {
    type: Number,
    default: 100,
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits(["added-to-group", "next-step"]);

const rooms = ref([]);
const dayType = ref(DateTime.now().weekday % 2 === 0 ? "even" : "odd");
const groups = ref([]);
const slots = ref([]);
const schedule = ref({});
const selectedGroup = ref(null);
const loading = ref(false);
const slotsRef = ref(null);
const isFullscreen = ref(false);
const isDragging = ref(false);
const dragStart = ref({
  x: 0,
  y: 0,
  scrollLeft: 0,
  scrollTop: 0,
});

function handleGroupSelected(group) {
  selectedGroup.value = group.id;
}

function toMinutes(t) {
  if (!t) return Number.POSITIVE_INFINITY;
  const [hh = "0", mm = "0"] = t.split(":");
  return Number(hh) * 60 + Number(mm);
}

function extractAndSetTimeSlots(groupsData) {
  if (!groupsData || groupsData.length === 0) {
    slots.value = [];
    rooms.value = [];
    schedule.value = {};
    return;
  }

  const timeSet = new Set();
  const roomSet = new Set();
  const newSchedule = {};

  // collect unique times
  groupsData.forEach((roomData) => {
    roomSet.add(roomData.room);
    roomData.groups.forEach((group) => {
      if (group.lesson_start_time) timeSet.add(group.lesson_start_time);
      if (group.lesson_end_time) timeSet.add(group.lesson_end_time);
    });
  });

  // sort chronologically (not lexicographically)
  const sortedSlots = Array.from(timeSet).sort(
    (a, b) => toMinutes(a) - toMinutes(b)
  );
  slots.value = sortedSlots;
  rooms.value = Array.from(roomSet).sort();

  // initialize grid
  rooms.value.forEach((room) => {
    newSchedule[room] = {};
    sortedSlots.forEach((slot) => {
      newSchedule[room][slot] = null;
    });
  });

  // place groups
  groupsData.forEach((roomData) => {
    const roomName = roomData.room;
    roomData.groups.forEach((group) => {
      const start = group.lesson_start_time;
      const end = group.lesson_end_time;

      if (!start || !end) return;

      const startIndex = sortedSlots.indexOf(start);
      const endIndex = sortedSlots.indexOf(end);

      // validate indices
      if (startIndex === -1 || endIndex === -1) return;
      if (endIndex < startIndex) return;

      // EXCLUSIVE end: cover up to but not including endIndex
      // if start==end (weird edge), still show 1 cell
      const colspan = Math.max(1, endIndex - startIndex);

      // Check if there's already a group at this time slot
      const existingSlot = newSchedule[roomName][sortedSlots[startIndex]];

      if (
        existingSlot &&
        existingSlot !== "covered" &&
        !Array.isArray(existingSlot)
      ) {
        // Convert single group to array of groups
        newSchedule[roomName][sortedSlots[startIndex]] = [
          existingSlot,
          {
            ...group,
            colspan,
          },
        ];
      } else if (Array.isArray(existingSlot)) {
        // Add to existing array of groups
        existingSlot.push({
          ...group,
          colspan,
        });
      } else {
        // Place the visible cell at the start
        newSchedule[roomName][sortedSlots[startIndex]] = {
          ...group,
          colspan,
        };
      }

      // mark covered cells AFTER start, up to (but NOT including) end
      for (let i = startIndex + 1; i < startIndex + colspan; i++) {
        if (i < sortedSlots.length) {
          newSchedule[roomName][sortedSlots[i]] = "covered";
        }
      }
    });
  });

  schedule.value = newSchedule;
}

async function getGroups() {
  // Get branch_id with fallback for kurator
  let currentBranchId = branchId.value;
  if (!currentBranchId) {
    currentBranchId = await getBranchId();
  }

  if (!currentBranchId) {
    console.warn("Branch ID not available");
    return;
  }

  loading.value = true;

  groups.value = await api
    .get("/courses/teachers_schedule", {
      params: {
        branch_id: currentBranchId,
        days: dayType.value,
      },
    })
    .then((res) => res.data)
    .catch((err) => {
      ElMessage.error(err?.message || "Xatolik yuz berdi");
      return [];
    });
  extractAndSetTimeSlots(groups.value);
  loading.value = false;
}

function toggleFullscreen() {
  if (!document.fullscreenElement) {
    slotsRef.value?.requestFullscreen();
    isFullscreen.value = true;
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
      isFullscreen.value = false;
    }
  }
}

function handleFullscreenChange() {
  isFullscreen.value =
    !!document.fullscreenElement &&
    document.fullscreenElement === slotsRef.value;
}

onMounted(() => {
  //   getRooms();
  // Only fetch groups if already expanded
  if (props.isExpanded) {
    getGroups();
  }
  window.addEventListener("fullscreenchange", handleFullscreenChange);
});

onUnmounted(() => {
  window.removeEventListener("fullscreenchange", handleFullscreenChange);
  if (
    document.fullscreenElement === slotsRef.value &&
    document.exitFullscreen
  ) {
    document.exitFullscreen().catch(() => {});
  }
});

const changeLeadStatus = async () => {
  await apiV2
    .patch("/leads/change-status", null, {
      params: {
        student_id: props.leadInfo.id,
        status: 1,
        type: "lead",
      },
    })
    .then(() => {
      emits("next-step", props.leadInfo);
    })
    .catch((err) => {
      ElMessage.error(err?.message);
    });
};

const addLeadToGroup = async () => {
  if (!selectedGroup.value) {
    changeLeadStatus();
    return;
  }

  loading.value = true;

  const body = {
    student_id: props.leadInfo.id,
    group_id: selectedGroup.value,
  };
  const selectedAddDate = localStorage.getItem("selected_add_date");
  if (selectedAddDate) {
    body.added_at = selectedAddDate;
  }

  await apiV2
    .post(`/groups/${selectedGroup.value}/students/${props.leadInfo.id}`, body)
    .then(() => {
      ElMessage.success("Guruhga muvaffaqiyatli qo'shildi");
      if (props.hasAlert) {
        changeLeadStatus();
      } else {
        loading.value = false;
        emits("added-to-group");
      }
    })
    .catch((err) => {
      ElMessage.error(err);
    });

  loading.value = false;
};

function isInteractiveElement(el) {
  if (!el) return false;
  return !!el.closest(
    "button, a, input, label, .schedule-group, .el-radio, .el-radio__label"
  );
}

function onMouseDown(e) {
  const el = slotsRef.value;
  if (!el) return;
  if (isInteractiveElement(e.target)) return;
  isDragging.value = true;
  dragStart.value = {
    x: e.clientX,
    y: e.clientY,
    scrollLeft: el.scrollLeft,
    scrollTop: el.scrollTop,
  };
  // Force a visible cursor globally while dragging (use widely supported 'move')
  document.body.style.cursor = "move";
  document.body.style.userSelect = "none";
}

function onMouseMove(e) {
  if (!isDragging.value) return;
  const el = slotsRef.value;
  if (!el) return;
  e.preventDefault();
  const dx = e.clientX - dragStart.value.x;
  const dy = e.clientY - dragStart.value.y;
  el.scrollLeft = dragStart.value.scrollLeft - dx;
  el.scrollTop = dragStart.value.scrollTop - dy;
}

function endDrag() {
  isDragging.value = false;
  // Restore defaults
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
}

function onMouseUp() {
  endDrag();
}

function onMouseLeave() {
  endDrag();
}

function onKeyDown(e) {
  const el = slotsRef.value;
  if (!el) return;
  const baseStep = 120;
  const step = e.shiftKey ? baseStep * 3 : baseStep;
  if (e.key === "ArrowLeft") {
    el.scrollLeft -= step;
    e.preventDefault();
  } else if (e.key === "ArrowRight") {
    el.scrollLeft += step;
    e.preventDefault();
  } else if (e.key === "ArrowUp") {
    el.scrollTop -= step;
    e.preventDefault();
  } else if (e.key === "ArrowDown") {
    el.scrollTop += step;
    e.preventDefault();
  }
}

function focusSlots() {
  const el = slotsRef.value;
  if (!el) return;
  try {
    // Prevent the browser from scrolling the element into view on focus
    el.focus({ preventScroll: true });
  } catch {
    el.focus();
  }
}

watch(
  () => branchId.value,
  async () => {
    // Only fetch if expanded
    if (props.isExpanded) {
      await getGroups();
    }
  }
);

// Watch for expansion state changes
watch(
  () => props.isExpanded,
  async (newValue) => {
    // Only fetch when expanded becomes true
    if (newValue) {
      await getGroups();
    }
  }
);
</script>

<style lang="scss">
.add-group {
  flex: 1;
  position: relative;
  width: 100%;
  padding: 6px 8px 0;

  .add-group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0 6px;
    min-height: 28px;

    .el-radio {
      height: 24px;
      margin-right: 16px;
    }
  }

  .alert-container {
    background: #fdfde9;
    padding: 10px;
    border-radius: 4px;
    border: 0.4px solid #c49d0a;
    display: inline-block;

    p {
      font-size: 12px;
      color: #888888;
    }
  }

  .slots {
    width: 100%;
    overflow: auto;
    height: calc(100vh - 200px);
    cursor: grab;
    cursor: -webkit-grab;
    cursor: -moz-grab;
  }
  .slots.is-dragging {
    /* Use 'move' for maximum cross-platform visibility while dragging */
    cursor: move !important;
    user-select: none;
  }
  .slots.is-dragging * {
    cursor: move !important;
  }

  .slots-table {
    width: 100%;
    border-collapse: collapse;
    background-color: white;

    thead {
      position: sticky;
      top: 0;
      z-index: 11;

      th {
        padding: 6px 4px;
        border: 0.6px solid #d1f6fc;
        background-color: #edfcfe;
        color: #10a5c6;
        font-size: 12px;
        font-weight: 500;

        &:first-child {
          position: sticky;
          left: 0;
          z-index: 11;
          width: 44px;
          min-width: 44px;
        }
      }
    }

    .room-name {
      padding: 4px;
      border: 0.6px solid #e7e7e7;
      background-color: #f6f6f6;
      text-align: center;
      position: sticky;
      left: 0;
      z-index: 1;
      font-size: 12px;
      font-weight: 500;
      width: 44px;
      min-width: 44px;
    }

    tbody {
      tr {
        td {
          padding: 3px;
          border: 0.6px solid #e7e7e7;
          vertical-align: top;
        }

        td.group-cell {
          min-width: 100px;
        }

        td.empty-cell {
          min-width: 22px;
          padding: 0;
        }
      }
    }
  }

  .multiple-groups {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;

    .multiple-group-item {
      flex: 1;
      min-height: 0;

      .schedule-group {
        padding: 6px;
        font-size: 14px;

        .schedule-group__header {
          .schedule-group__header-group-name {
            font-size: 16px;
          }
        }

        .schedule-group__body {
          .schedule-group__body-date-duration {
            .schedule-group__body-date,
            .schedule-group__body-duration {
              font-size: 10px;
            }
          }

          .schedule-group__body-teacher-name {
            font-size: 10px;
            margin-top: 8px;
          }
        }
      }
    }
  }
}
</style>
